import { useQuery } from "@tanstack/react-query";
import { ArrowDown, ArrowUp, ChevronsUpDown, Download, Gauge, Search } from "lucide-react";
import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import {
  descargarMargenCapacidadXlsx,
  getMargenCapacidad,
  getOfertasCapacidadRango,
  type MargenCapacidadFila,
} from "@/lib/api-mercado";

const SISTEMAS = ["SIN", "BCA", "BCS"] as const;
const COLOR_SISTEMA: Record<string, string> = {
  SIN: "#1d4ed8",
  BCA: "#059669",
  BCS: "#d97706",
};
// Trazo de la línea de % de reserva: tono oscuro del MISMO sistema (la
// identidad la da el color; la medida la da la marca: barra = MW, línea = %).
const COLOR_PCT: Record<string, string> = {
  SIN: "#1e3a8a",
  BCA: "#064e3b",
  BCS: "#78350f",
};
const MESES_G = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];
// Rombos del % de reserva operativa CENACE en la curva HC, por HC y su día:
//   verde  = el pico de demanda de CENACE cae en la fecha y hora de ESA HC;
//   ámbar  = no cae en esa HC, pero sí en OTRA HC del mismo día dentro del top;
//   rojo   = ninguna HC de ese día en el top contiene el pico de CENACE.
// Sin registro MRO para el día no hay rombo.
type EstadoMro = "verde" | "ambar" | "rojo";
const COLOR_MRO: Record<EstadoMro, string> = {
  verde: "#16a34a",
  ambar: "#d97706",
  rojo: "#dc2626",
};
const TEXTO_MRO: Record<EstadoMro, string> = {
  verde: "el pico cae en esa hora",
  ambar: "el pico cae en otra HC de ese día",
  rojo: "ninguna HC de ese día contiene el pico",
};
const CLASE_MRO: Record<EstadoMro, string> = {
  verde: "text-green-700",
  ambar: "text-amber-700",
  rojo: "text-red-700",
};

// Forma del rombo: Recharts pasa cx/cy y el renglón completo como payload; el
// color sale del estado guardado en `${sistema}_mro_estado`. Sin estado (el día
// no tiene MRO) no se dibuja nada.
const romboMro =
  (estadoKey: string) =>
  ({ cx, cy, payload }: { cx?: number; cy?: number; payload?: Record<string, unknown> }) => {
    const estado = payload?.[estadoKey] as EstadoMro | null | undefined;
    if (!estado || cx == null || cy == null || Number.isNaN(cx) || Number.isNaN(cy)) return <g />;
    const r = 6;
    return (
      <polygon
        points={`${cx},${cy - r} ${cx + r},${cy} ${cx},${cy + r} ${cx - r},${cy}`}
        fill={COLOR_MRO[estado]}
        stroke="#ffffff"
        strokeWidth={1}
      />
    );
  };

const num = (n: number | null, d = 3) =>
  n == null
    ? "—"
    : new Intl.NumberFormat("es-MX", { minimumFractionDigits: d, maximumFractionDigits: d }).format(n);
const pct = (n: number | null, d = 2) => (n == null ? "—" : `${num(n, d)} %`);

// Color del % de reserva operativa de CENACE según sus estados operativos:
// < 3 % emergencia (rojo), < 6 % alerta (ámbar), el resto normal.
const colorReservaCenace = (p: number | null) =>
  p == null ? "text-slate-400" : p < 3 ? "text-red-700 font-semibold" : p < 6 ? "text-amber-700" : "text-slate-700";

// Ficha de una hora crítica para los tooltips de las dos gráficas: la hora, su
// margen y lo que CENACE reportó para ESE DÍA en el MRO (hora pico, demanda
// máxima instantánea y reserva operativa). `titulo` trae el # y el sistema.
function FichaHC({
  f,
  titulo,
  estado,
}: {
  f: MargenCapacidadFila;
  titulo: string;
  estado: EstadoMro | null;
}) {
  const hayMro = f.mro_reserva_pct_dia != null && estado != null;
  const fila = (k: string, v: ReactNode) => (
    <>
      <span className="text-slate-500">{k}</span>
      <span className="text-right tabular-nums">{v}</span>
    </>
  );
  return (
    <div className="min-w-[250px]">
      <div className="font-semibold mb-1">{titulo}</div>
      <div className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-0.5">
        {fila("Fecha y hora", `${f.fecha} · ${String(f.hora).padStart(2, "0")}:00 h`)}
        {fila(
          "Margen",
          <b className={f.margen_mw < 0 ? "text-red-700" : ""}>{num(f.margen_mw)} MW</b>,
        )}
        {fila(
          "% margen de reserva",
          <b className={(f.margen_pct ?? 0) < 0 ? "text-red-700" : ""}>{pct(f.margen_pct)}</b>,
        )}
      </div>
      <div className="mt-1.5 pt-1 border-t text-slate-500 font-medium">
        CENACE (MRO) · {f.fecha}
      </div>
      {hayMro ? (
        <div className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-0.5">
          {fila(
            "Hora pico",
            <>
              {f.mro_hora_pico}{" "}
              <span className={CLASE_MRO[estado ?? "rojo"]}>
                ({estado === "verde"
                  ? "cae en esta hora"
                  : estado === "ambar"
                    ? "cae en otra HC de este día"
                    : "en ninguna HC de este día"})
              </span>
            </>,
          )}
          {fila("Demanda máx. inst.", `${num(f.mro_dem_max_mw_dia, 0)} MW`)}
          {fila(
            "Reserva operativa",
            <>
              {num(f.mro_reserva_mw_dia, 2)} MW ·{" "}
              <b className={colorReservaCenace(f.mro_reserva_pct_dia)}>{pct(f.mro_reserva_pct_dia)}</b>
            </>,
          )}
        </div>
      ) : (
        <div className="text-slate-400">Sin registro MRO para ese día</div>
      )}
    </div>
  );
}

// Top-N de horas con MENOR margen (capacidad ofertada al MTR − demanda real).
// La demanda combina balance + operativa igual que el Portal (/demanda).
export function MargenCapacidadPage() {
  // Reporte ANUAL: se elige el año (0 = aún no se conoce el rango con datos).
  const [anio, setAnio] = useState(0);
  const [sistemas, setSistemas] = useState<string[]>(["SIN"]);
  const [top, setTop] = useState(100);
  // Apagado por default: la capacidad es SOLO la ofertada al MTR. Encendido,
  // se le suma el intercambio neto (+import − export) del balance.
  const [intercambio, setIntercambio] = useState(false);
  const [bajando, setBajando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // El rango con datos lo manda la CAPACIDAD (la demanda llega más lejos, pero
  // sin capacidad no hay margen que calcular). Default: el último año con datos.
  const rangoQ = useQuery({
    queryKey: ["ofertas-capacidad-rango"],
    queryFn: getOfertasCapacidadRango,
    staleTime: 60 * 60_000,
  });
  const anioMin = rangoQ.data?.mes_ini ? Number(rangoQ.data.mes_ini.slice(0, 4)) : null;
  const anioMax = rangoQ.data?.mes_fin ? Number(rangoQ.data.mes_fin.slice(0, 4)) : null;
  const anios =
    anioMin != null && anioMax != null
      ? Array.from({ length: anioMax - anioMin + 1 }, (_, i) => anioMax - i)
      : [];
  useEffect(() => {
    if (anioMax != null) setAnio((a) => (a ? a : anioMax));
  }, [anioMax]);

  // La consulta NO se dispara al cambiar filtros (un año completo agrega
  // millones de renglones): solo al presionar "Buscar", que congela filtros.
  const [aplicado, setAplicado] = useState<{
    anio: number;
    sistemas: string[];
    top: number;
    intercambio: boolean;
  } | null>(null);

  const q = useQuery({
    queryKey: [
      "margen-capacidad",
      aplicado?.anio,
      aplicado?.sistemas.join(","),
      aplicado?.top,
      aplicado?.intercambio,
    ],
    queryFn: () =>
      getMargenCapacidad(aplicado!.anio, aplicado!.sistemas, aplicado!.top, aplicado!.intercambio),
    enabled: !!aplicado,
    retry: false,
  });
  const filas = q.data?.filas ?? [];
  // La columna extra se muestra según el corte BUSCADO (no el checkbox a medio editar).
  const conIntercambio = aplicado?.intercambio ?? false;

  const filtrosListos = sistemas.length > 0 && anio > 0;
  const filtrosCambiaron =
    !!aplicado &&
    (aplicado.anio !== anio ||
      aplicado.top !== top ||
      aplicado.intercambio !== intercambio ||
      aplicado.sistemas.join(",") !== sistemas.join(","));

  const buscar = () => {
    if (!filtrosListos) return;
    setError(null);
    setAplicado({ anio, sistemas: [...sistemas], top, intercambio });
  };

  // Orden de la tabla. La posición (#) se congela ANTES de reordenar y sigue el
  // margen DESCENDENTE: #1 = mayor margen del top, #N = la hora más apretada
  // (mismo orden que la curva HC). El API manda las filas de menor a mayor
  // margen, por eso la posición es n − i.
  const [sortKey, setSortKey] = useState<keyof MargenCapacidadFila | "posicion">("posicion");
  const [sortDir, setSortDir] = useState<1 | -1>(1);
  const toggleSort = (k: keyof MargenCapacidadFila | "posicion") => {
    if (sortKey === k) setSortDir((d) => (d === 1 ? -1 : 1));
    else {
      setSortKey(k);
      setSortDir(1);
    }
  };
  const filasOrdenadas = useMemo(() => {
    const n = filas.length;
    const conPos = filas.map((f, i) => ({ ...f, posicion: n - i }));
    if (sortKey === "posicion") return sortDir === 1 ? conPos : [...conPos].reverse();
    return [...conPos].sort((a, b) => {
      const va = a[sortKey];
      const vb = b[sortKey];
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      const cmp =
        typeof va === "number" && typeof vb === "number"
          ? va - vb
          : String(va).localeCompare(String(vb), "es");
      return (cmp || a.posicion - b.posicion) * sortDir;
    });
  }, [filas, sortKey, sortDir]);

  const toggleSistema = (s: string) =>
    setSistemas((prev) =>
      prev.includes(s) ? (prev.length > 1 ? prev.filter((x) => x !== s) : prev) : [...prev, s],
    );

  const descargar = async () => {
    if (!aplicado) return;
    setBajando(true);
    setError(null);
    try {
      // Descarga EXACTAMENTE el corte buscado (no los filtros a medio editar).
      await descargarMargenCapacidadXlsx(
        aplicado.anio, aplicado.sistemas, aplicado.top, aplicado.intercambio,
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : "No se pudo generar el Excel");
    } finally {
      setBajando(false);
    }
  };

  // Línea de tiempo: cada hora del top como punto (x = momento del año,
  // y = margen), una serie por sistema. Los ticks del eje X son los meses.
  const puntosPorSistema = useMemo(() => {
    const m = new Map<string, (MargenCapacidadFila & { t: number; pos: number })[]>();
    const n = filas.length;
    filas.forEach((f, i) => {
      const t = new Date(`${f.fecha}T00:00:00`).getTime() + (f.hora - 1) * 3_600_000;
      const arr = m.get(f.sistema) ?? [];
      // `pos` = el # de la tabla (margen descendente; el API viene ascendente).
      arr.push({ ...f, t, pos: n - i });
      m.set(f.sistema, arr);
    });
    return m;
  }, [filas]);
  // El eje del tiempo corresponde al año BUSCADO (las filas son de ese año),
  // no al del selector, que puede estar a medio editar.
  const anioGrafica = aplicado?.anio ?? 0;

  // Estado del rombo MRO de cada HC (ver EstadoMro): días del top con al menos
  // una HC cuya hora contiene el pico de CENACE.
  const diasConPico = useMemo(() => {
    const s = new Set<string>();
    for (const f of filas) if (f.mro_dem_max_mw != null) s.add(`${f.sistema}|${f.fecha}`);
    return s;
  }, [filas]);
  const estadoMro = useCallback(
    (f: MargenCapacidadFila): EstadoMro | null =>
      f.mro_reserva_pct_dia == null
        ? null
        : f.mro_dem_max_mw != null
          ? "verde"
          : diasConPico.has(`${f.sistema}|${f.fecha}`)
            ? "ambar"
            : "rojo",
    [diasConPico],
  );

  // Curva de horas críticas (HC): el top ordenado de MAYOR a MENOR margen
  // (curva de duración, por sistema) + primera y última hora CRONOLÓGICA del
  // top, que delimitan el periodo HC de lo que va del año.
  const fmtHC = (f: MargenCapacidadFila) =>
    `${f.fecha} ${String(f.hora).padStart(2, "0")}:00`;
  const curvaHC = useMemo(() => {
    const porSistema = new Map<string, MargenCapacidadFila[]>();
    for (const f of filas) {
      const arr = porSistema.get(f.sistema) ?? [];
      arr.push(f);
      porSistema.set(f.sistema, arr);
    }
    const maxLen = Math.max(0, ...[...porSistema.values()].map((a) => a.length));
    // Un renglón por posición HC con TODAS las series (barras, % y rombos MRO).
    // Los rombos van sobre estos mismos datos y NO con `data` propia en el
    // Scatter: en Recharts 2.x un item con `data` propia reemplaza los datos de
    // la gráfica al armar el tooltip (y lo deja vacío).
    const puntos: Record<string, number | string | null>[] = Array.from(
      { length: maxLen },
      (_, i) => ({ hc: i + 1 }),
    );
    const detalle = new Map<string, MargenCapacidadFila[]>();
    const periodo = new Map<
      string,
      { primera: MargenCapacidadFila; ultima: MargenCapacidadFila }
    >();
    for (const [s, arr] of porSistema) {
      const desc = [...arr].sort((a, b) => b.margen_mw - a.margen_mw);
      desc.forEach((f, i) => {
        puntos[i][s] = f.margen_mw;
        puntos[i][`${s}_pct`] = f.margen_pct; // mismo orden: % de la misma hora
        // % de reserva operativa CENACE DEL DÍA (eje derecho) + estado del rombo.
        puntos[i][`${s}_mro`] = f.mro_reserva_pct_dia;
        puntos[i][`${s}_mro_estado`] = estadoMro(f);
      });
      detalle.set(s, desc);
      const crono = [...arr].sort((a, b) =>
        a.fecha === b.fecha ? a.hora - b.hora : a.fecha.localeCompare(b.fecha),
      );
      periodo.set(s, { primera: crono[0], ultima: crono[crono.length - 1] });
    }
    return { puntos, detalle, periodo };
  }, [filas, estadoMro]);
  const hayMro = filas.some((f) => f.mro_reserva_pct_dia != null);
  const ejeX = useMemo(() => {
    if (!anioGrafica) return { min: 0, max: 1, ticks: [] as number[] };
    return {
      min: new Date(anioGrafica, 0, 1).getTime(),
      max: new Date(anioGrafica + 1, 0, 1).getTime(),
      ticks: MESES_G.map((_, i) => new Date(anioGrafica, i, 1).getTime()),
    };
  }, [anioGrafica]);
  const hayNegativos = filas.some((f) => f.margen_mw < 0);

  // Ejes Y de la curva HC: MW a la izquierda y % a la derecha. Sin negativos
  // ambos arrancan en 0 y quedan alineados solos. Con negativos se extiende el
  // mínimo del eje que lo necesite para que el CERO quede a la misma altura en
  // los dos (así la línea roja de cero vale para las barras y para la línea).
  const dominios = useMemo(() => {
    type Dom = [number | "auto", number | "auto"];
    const auto: { mw: Dom; pct: Dom } = { mw: [0, "auto"], pct: [0, "auto"] };
    const mw = filas.map((f) => f.margen_mw);
    // El eje de % también carga los rombos del % CENACE (suelen ir más arriba).
    const pc = filas
      .flatMap((f) => [f.margen_pct, f.mro_reserva_pct_dia])
      .filter((v): v is number => v != null);
    if (!hayNegativos || mw.length === 0 || pc.length === 0) return auto;
    // Redondeo "bonito" hacia arriba (1-2-5 × 10^k) para los extremos.
    const nice = (v: number) => {
      if (v <= 0) return 0;
      const p = 10 ** Math.floor(Math.log10(v));
      const m = v / p;
      return (m <= 1 ? 1 : m <= 2 ? 2 : m <= 5 ? 5 : 10) * p;
    };
    const maxMw = nice(Math.max(0, ...mw));
    const maxPc = nice(Math.max(0, ...pc));
    const minMw = -nice(-Math.min(0, ...mw));
    const minPc = -nice(-Math.min(0, ...pc));
    if (maxMw <= 0 || maxPc <= 0) return { mw: ["auto", "auto"] as Dom, pct: ["auto", "auto"] as Dom };
    // Fracción del eje que queda debajo de cero: se toma la mayor y el otro
    // eje baja su mínimo hasta igualarla.
    const f = Math.max(-minMw / (maxMw - minMw), -minPc / (maxPc - minPc));
    const piso = (max: number) => -(f * max) / (1 - f);
    return {
      mw: [Math.min(minMw, piso(maxMw)), maxMw] as Dom,
      pct: [Math.min(minPc, piso(maxPc)), maxPc] as Dom,
    };
  }, [filas, hayNegativos]);

  // Columnas de la tabla. Las CENACE (MRO) van junto al % de reserva para
  // comparar las dos lecturas del mismo día lado a lado.
  type Col = { k: typeof sortKey; label: string; right?: boolean; hint?: string };
  const columnas: Col[] = [
    {
      k: "posicion",
      label: "#",
      right: true,
      hint: "Por margen descendente: 1 = mayor margen del top, la última = la hora más apretada (como la curva HC)",
    },
    { k: "sistema", label: "Sistema" },
    { k: "fecha", label: "Fecha" },
    { k: "hora", label: "Hora", right: true, hint: "Hora 1–24: la hora h cubre de (h−1):00 a h:00" },
    { k: "capacidad_mw", label: "Capacidad total ofertada (MW)", right: true },
    ...(conIntercambio
      ? [{ k: "intercambio_mw", label: "+Import − export (MW)", right: true } as Col]
      : []),
    { k: "demanda_mw", label: "Demanda real (MW)", right: true },
    { k: "margen_mw", label: "Margen (MW)", right: true },
    {
      k: "margen_pct",
      label: "Margen de reserva (%)",
      right: true,
      hint: "Margen ÷ capacidad total ofertada al MTR",
    },
    {
      k: "mro_hora_pico",
      label: "Hora pico CENACE",
      right: true,
      hint: "Instante de la demanda máxima instantánea del día (reporte MRO de CENACE). Sale en todas las filas del día",
    },
    {
      k: "mro_dem_max_mw",
      label: "Dem. máx. inst. CENACE (MW)",
      right: true,
      hint: "Demanda máxima instantánea del día. Solo en la fila cuya hora contiene el pico",
    },
    {
      k: "mro_reserva_mw",
      label: "Reserva operativa CENACE (MW)",
      right: true,
      hint: "Reserva operativa en el instante del pico. Solo en la fila cuya hora contiene el pico",
    },
    {
      k: "mro_reserva_pct",
      label: "Reserva operativa CENACE (%)",
      right: true,
      hint: "Reserva ÷ demanda máxima instantánea (NO ÷ capacidad). Rojo < 3 % emergencia, ámbar < 6 % alerta",
    },
  ];

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
          <Gauge className="w-5 h-5 text-slate-500" /> Margen capacidad − demanda (MTR)
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Las <b>{top} horas más apretadas del año</b>: capacidad total ofertada al MTR menos
          la demanda real (balance + operativa, la misma del Portal), numeradas de mayor a
          menor margen (la última fila es la hora más apretada).
          El <b>% de margen de reserva</b> es ese margen dividido entre la capacidad total
          ofertada. Margen negativo = la demanda superó la <i>oferta</i> al MTR (se cubrió con
          importaciones, eólica sobre pronóstico y despacho de emergencia); activa el
          checkbox para sumar el intercambio neto a la capacidad. CENACE publica las
          ofertas al MTR con <b>~60 días de rezago</b>, por eso el reporte siempre llega
          hasta ~2 meses antes de hoy aunque la demanda esté al día.
        </p>
        <p className="text-sm text-slate-500 mt-1">
          Las columnas <b>CENACE</b> vienen de su reporte diario de Margen de Reserva Operativa
          (MRO): la <b>hora pico</b> (instante de la demanda máxima instantánea) sale en todas
          las filas del día; la demanda máxima y la reserva operativa <b>solo en la fila cuya
          hora contiene ese pico</b>, porque ahí es donde CENACE las mide. Ojo: su % es reserva
          ÷ demanda máxima, no ÷ capacidad ofertada como el nuestro.
        </p>
      </div>

      <div className="bg-white border rounded-lg px-4 py-3 flex flex-wrap items-end gap-4">
        <label className="block">
          <span className="text-xs font-medium text-slate-600">Año</span>
          <select
            className="mt-1 border rounded px-2 py-1.5 text-sm"
            value={anio || ""}
            onChange={(e) => setAnio(Number(e.target.value))}
            disabled={anios.length === 0}
          >
            {anios.length === 0 && <option value="">…</option>}
            {anios.map((a) => (
              <option key={a} value={a}>{a}</option>
            ))}
          </select>
        </label>
        <div>
          <span className="block text-xs font-medium text-slate-600 mb-1">Sistema</span>
          <div className="flex gap-1.5">
            {SISTEMAS.map((s) => {
              const on = sistemas.includes(s);
              return (
                <button
                  key={s}
                  type="button"
                  onClick={() => toggleSistema(s)}
                  className={`text-sm border rounded px-3 py-1.5 ${
                    on
                      ? "bg-blue-600 text-white border-blue-600"
                      : "text-slate-600 hover:bg-slate-50"
                  }`}
                >
                  {s}
                </button>
              );
            })}
          </div>
        </div>
        <label className="block">
          <span className="text-xs font-medium text-slate-600">Top</span>
          <select
            className="mt-1 border rounded px-2 py-1.5 text-sm"
            value={top}
            onChange={(e) => setTop(Number(e.target.value))}
          >
            {[25, 50, 100, 200, 500].map((n) => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
        </label>
        <label
          className="flex items-center gap-2 text-sm text-slate-600 pb-2 cursor-pointer select-none"
          title="Suma a la capacidad el intercambio neto del balance (la oferta al MTR no incluye importaciones)"
        >
          <input
            type="checkbox"
            className="w-4 h-4 accent-blue-600"
            checked={intercambio}
            onChange={(e) => setIntercambio(e.target.checked)}
          />
          Incluir +importaciones − exportaciones
        </label>
        <button
          type="button"
          onClick={buscar}
          disabled={!filtrosListos || q.isFetching}
          title={filtrosListos ? undefined : "Elige el año y al menos un sistema"}
          className="inline-flex items-center gap-2 bg-blue-600 text-white rounded px-4 py-2 text-sm hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Search className="w-4 h-4" /> {q.isFetching ? "Buscando…" : "Buscar"}
        </button>
        <button
          type="button"
          onClick={descargar}
          disabled={bajando || q.isLoading || filas.length === 0}
          title={aplicado ? "Descarga el corte buscado" : "Primero presiona Buscar"}
          className="inline-flex items-center gap-2 bg-emerald-600 text-white rounded px-4 py-2 text-sm hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Download className="w-4 h-4" /> {bajando ? "Generando…" : "Descargar Excel"}
        </button>
        <span
          className="text-[11px] text-slate-400 pb-2 ml-auto"
          title="CENACE publica las ofertas al MTR con ~60 días de rezago; el ETL descarga todo lo ya publicado"
        >
          {q.isFetching ? "Cargando…" : aplicado ? `${filas.length} filas` : "sin buscar"}
          {rangoQ.data?.mes_ini &&
            ` · capacidad disponible ${rangoQ.data.mes_ini} a ${rangoQ.data.mes_fin} (rezago CENACE ~60 días)`}
        </span>
      </div>

      {filtrosCambiaron && !q.isFetching && (
        <div className="bg-blue-50 border border-blue-200 text-blue-800 rounded p-3 text-sm">
          Cambiaste los filtros — se sigue mostrando el top {aplicado!.top} de {aplicado!.anio}{" "}
          ({aplicado!.sistemas.join(", ")}). Presiona <b>Buscar</b> para actualizar.
        </div>
      )}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded p-3 text-sm">{error}</div>
      )}
      {q.isError && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded p-3 text-sm">
          {(q.error as Error)?.message ?? "No se pudo consultar el reporte."}
        </div>
      )}

      {/* Curva de horas críticas: el top ordenado de mayor a menor margen */}
      {filas.length > 0 && (
        <div className="bg-white border rounded-lg p-4">
          <div className="mb-2">
            <h3 className="text-sm font-semibold text-slate-800">
              Curva de horas críticas (HC) — top {filas.length} de {anioGrafica}
            </h3>
            <p className="text-[11px] text-slate-500">
              Las {filas.length} horas con menor margen de reserva de lo que va del año,
              ordenadas de <b>mayor a menor margen</b>: el extremo derecho es la hora más
              apretada. Los <b>rombos</b> son el % de reserva operativa que CENACE reportó
              para el día de cada HC (medido en el pico): <b className="text-green-700">verde</b>{" "}
              si el pico cae en esa misma hora, <b className="text-amber-700">ámbar</b> si cae en
              otra HC de ese día dentro del top, <b className="text-red-700">rojo</b> si ninguna HC
              de ese día contiene el pico; sin registro MRO no hay rombo.
              Abajo, la primera y la última hora (cronológicas) del top, que delimitan el
              periodo HC.
            </p>
            {[...curvaHC.periodo.entries()].map(([s, p]) => (
              <p key={s} className="text-[11px] text-slate-600 mt-0.5">
                <span
                  className="inline-block w-2 h-2 rounded-full mr-1.5"
                  style={{ backgroundColor: COLOR_SISTEMA[s] ?? "#64748b" }}
                />
                <b>{s}</b> · periodo HC: primera hora <b>{fmtHC(p.primera)}</b> · última
                hora <b>{fmtHC(p.ultima)}</b>
              </p>
            ))}
          </div>
          <div className="h-[340px]">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart
                data={curvaHC.puntos}
                barCategoryGap="12%"
                margin={{ top: 10, right: 8, left: 0, bottom: 16 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis
                  dataKey="hc"
                  interval={Math.max(0, Math.ceil(curvaHC.puntos.length / 20) - 1)}
                  tick={{ fontSize: 11, fill: "#475569" }}
                  label={{
                    value: "Posición (1 = mayor margen del top · derecha = más apretada)",
                    position: "insideBottom",
                    offset: -10,
                    style: { fontSize: 11, fill: "#64748b" },
                  }}
                />
                <YAxis
                  yAxisId="mw"
                  type="number"
                  domain={dominios.mw}
                  tick={{ fontSize: 11, fill: "#475569" }}
                  width={76}
                  tickFormatter={(v) =>
                    new Intl.NumberFormat("es-MX", { maximumFractionDigits: 0 }).format(Number(v))
                  }
                  label={{
                    value: "Margen (MW)",
                    angle: -90,
                    position: "insideLeft",
                    style: { fontSize: 11, fill: "#64748b" },
                  }}
                />
                {/* Eje secundario: % de margen de reserva (margen ÷ capacidad ofertada) */}
                <YAxis
                  yAxisId="pct"
                  orientation="right"
                  type="number"
                  domain={dominios.pct}
                  tick={{ fontSize: 11, fill: "#475569" }}
                  width={60}
                  tickFormatter={(v) =>
                    `${new Intl.NumberFormat("es-MX", { maximumFractionDigits: 1 }).format(Number(v))} %`
                  }
                  label={{
                    value: "Margen de reserva (%)",
                    angle: 90,
                    position: "insideRight",
                    style: { fontSize: 11, fill: "#64748b" },
                  }}
                />
                {hayNegativos && (
                  <ReferenceLine
                    yAxisId="mw"
                    y={0}
                    stroke="#dc2626"
                    strokeDasharray="5 3"
                    strokeWidth={1.5}
                  />
                )}
                <Tooltip
                  cursor={{ fill: "#f1f5f9" }}
                  content={({ active, payload, label }) => {
                    if (!active || !payload?.length) return null;
                    // Un renglón por sistema (las series "_pct" son la misma hora).
                    const enPayload = new Set(payload.map((e) => String(e.dataKey)));
                    const sistemasHC = [...curvaHC.detalle.keys()].filter((s) => enPayload.has(s));
                    return (
                      <div className="bg-white border rounded shadow px-3 py-2 text-xs space-y-2">
                        {sistemasHC.map((s) => {
                          const f = curvaHC.detalle.get(s)?.[Number(label) - 1];
                          if (!f) return null;
                          return (
                            <FichaHC
                              key={s}
                              f={f}
                              titulo={`HC #${label} · ${s}`}
                              estado={estadoMro(f)}
                            />
                          );
                        })}
                      </div>
                    );
                  }}
                />
                {[...curvaHC.detalle.keys()].map((s) => (
                  <Bar
                    key={s}
                    yAxisId="mw"
                    dataKey={s}
                    fill={COLOR_SISTEMA[s] ?? "#64748b"}
                    isAnimationActive={false}
                  />
                ))}
                {/* % de reserva sobre el eje derecho. Va en dos trazos: un halo
                    blanco debajo y el trazo oscuro del sistema encima, para que
                    se lea sobre las barras del mismo color. */}
                {[...curvaHC.detalle.keys()].flatMap((s) => [
                  <Line
                    key={`${s}-halo`}
                    yAxisId="pct"
                    type="monotone"
                    dataKey={`${s}_pct`}
                    stroke="#ffffff"
                    strokeWidth={5}
                    dot={false}
                    activeDot={false}
                    connectNulls
                    isAnimationActive={false}
                  />,
                  <Line
                    key={`${s}-pct`}
                    yAxisId="pct"
                    type="monotone"
                    dataKey={`${s}_pct`}
                    stroke={COLOR_PCT[s] ?? "#0f172a"}
                    strokeWidth={2}
                    dot={false}
                    activeDot={{ r: 4, strokeWidth: 0 }}
                    connectNulls
                    isAnimationActive={false}
                  />,
                ])}
                {/* % de reserva operativa de CENACE DEL DÍA: un rombo por HC
                    cuyo día tiene registro MRO, sobre el MISMO eje de % (derecho).
                    Color por estado (verde/ámbar/rojo, ver EstadoMro). Misma
                    `data` que las barras: sin `data` propia (rompe el tooltip). */}
                {hayMro &&
                  [...curvaHC.detalle.keys()].map((s) => (
                    <Scatter
                      key={`${s}-mro`}
                      yAxisId="pct"
                      dataKey={`${s}_mro`}
                      shape={romboMro(`${s}_mro_estado`)}
                      isAnimationActive={false}
                    />
                  ))}
              </ComposedChart>
            </ResponsiveContainer>
          </div>
          <div className="flex flex-wrap gap-x-5 gap-y-1 justify-center text-xs text-slate-600">
            {[...curvaHC.detalle.keys()].map((s) => (
              <span key={s} className="inline-flex items-center gap-3">
                <span className="inline-flex items-center gap-1.5">
                  <span
                    className="inline-block w-2.5 h-2.5 rounded-sm"
                    style={{ backgroundColor: COLOR_SISTEMA[s] ?? "#64748b" }}
                  />
                  {s} · margen (MW)
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <span
                    className="inline-block w-4 h-0.5 rounded"
                    style={{ backgroundColor: COLOR_PCT[s] ?? "#0f172a" }}
                  />
                  {s} · margen de reserva (%)
                </span>
              </span>
            ))}
            {hayMro && (
              <span
                className="inline-flex flex-wrap items-center gap-x-3 gap-y-1"
                title="Reserva ÷ demanda máxima instantánea del día, medida por CENACE en el instante del pico; un rombo por cada HC cuyo día tiene registro MRO"
              >
                <span className="text-slate-500">rombo = reserva operativa CENACE del día (%):</span>
                {(["verde", "ambar", "rojo"] as EstadoMro[]).map((e) => (
                  <span key={e} className="inline-flex items-center gap-1.5">
                    <span
                      className="inline-block w-2 h-2 rotate-45"
                      style={{ backgroundColor: COLOR_MRO[e] }}
                    />
                    {TEXTO_MRO[e]}
                  </span>
                ))}
              </span>
            )}
          </div>
        </div>
      )}

      <div className="bg-white border rounded-lg overflow-hidden">
        <div className="overflow-x-auto max-h-[70vh]">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-slate-50 shadow-[0_1px_0_0_#e2e8f0]">
              <tr className="text-xs text-slate-500">
                {columnas.map((c) => {
                  const activa = sortKey === c.k;
                  const Icono = activa ? (sortDir === 1 ? ArrowUp : ArrowDown) : ChevronsUpDown;
                  return (
                    <th key={c.k} className={`px-3 py-2 ${c.right ? "text-right" : "text-left"}`}>
                      <button
                        type="button"
                        onClick={() => toggleSort(c.k)}
                        title={c.hint ? `${c.hint}. Clic para ordenar` : "Ordenar por esta columna"}
                        className={`inline-flex items-center gap-1 hover:text-slate-700 ${
                          c.right ? "flex-row-reverse" : ""
                        } ${activa ? "text-slate-700 font-semibold" : ""}`}
                      >
                        <span>{c.label}</span>
                        <Icono className={`w-3 h-3 ${activa ? "text-slate-500" : "text-slate-300"}`} />
                      </button>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {!aplicado && !q.isFetching ? (
                <tr>
                  <td colSpan={columnas.length} className="px-4 py-6 text-sm text-slate-500">
                    Elige el año, los sistemas y el top, y presiona <b>Buscar</b> para
                    cargar la información (un año completo tarda ~15–30 s).
                  </td>
                </tr>
              ) : q.isFetching || rangoQ.isLoading ? (
                <tr>
                  <td colSpan={columnas.length} className="px-4 py-6 text-sm text-slate-500">Cargando…</td>
                </tr>
              ) : filas.length === 0 ? (
                <tr>
                  <td colSpan={columnas.length} className="px-4 py-6 text-sm text-slate-500">
                    Sin horas con capacidad Y demanda en el año buscado.
                  </td>
                </tr>
              ) : (
                filasOrdenadas.map((f) => (
                  <tr
                    key={`${f.sistema}-${f.fecha}-${f.hora}`}
                    className="border-b last:border-0 hover:bg-slate-50"
                  >
                    <td className="px-3 py-1.5 text-right text-slate-400 tabular-nums">{f.posicion}</td>
                    <td className="px-3 py-1.5 font-medium">{f.sistema}</td>
                    <td className="px-3 py-1.5 tabular-nums">{f.fecha}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums">{f.hora}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums">{num(f.capacidad_mw)}</td>
                    {conIntercambio && (
                      <td className="px-3 py-1.5 text-right tabular-nums text-slate-500">
                        {num(f.intercambio_mw)}
                      </td>
                    )}
                    <td className="px-3 py-1.5 text-right tabular-nums">{num(f.demanda_mw)}</td>
                    <td
                      className={`px-3 py-1.5 text-right tabular-nums font-semibold ${
                        f.margen_mw < 0 ? "text-red-700" : ""
                      }`}
                    >
                      {num(f.margen_mw)}
                    </td>
                    <td
                      className={`px-3 py-1.5 text-right tabular-nums ${
                        (f.margen_pct ?? 0) < 0 ? "text-red-700" : "text-slate-700"
                      }`}
                    >
                      {pct(f.margen_pct)}
                    </td>
                    {/* CENACE (MRO): la hora pico siempre; las métricas solo si esta
                        fila es la hora que contiene el pico (si no, vienen null). */}
                    <td
                      className={`px-3 py-1.5 text-right tabular-nums ${
                        f.mro_dem_max_mw != null ? "text-slate-800 font-medium" : "text-slate-400"
                      }`}
                      title={
                        f.mro_dem_max_mw != null
                          ? "El pico de demanda de CENACE cae dentro de esta hora"
                          : "El pico de demanda de CENACE cae en otra hora del día"
                      }
                    >
                      {f.mro_hora_pico ?? "—"}
                    </td>
                    <td className="px-3 py-1.5 text-right tabular-nums text-slate-700">
                      {num(f.mro_dem_max_mw)}
                    </td>
                    <td className="px-3 py-1.5 text-right tabular-nums text-slate-700">
                      {num(f.mro_reserva_mw)}
                    </td>
                    <td className={`px-3 py-1.5 text-right tabular-nums ${colorReservaCenace(f.mro_reserva_pct)}`}>
                      {pct(f.mro_reserva_pct)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Línea de tiempo del año: cada hora del top como punto */}
      {filas.length > 0 && (
        <div className="bg-white border rounded-lg p-4">
          <div className="mb-2">
            <h3 className="text-sm font-semibold text-slate-800">
              Las {filas.length} horas en el tiempo — {anioGrafica}
            </h3>
            <p className="text-[11px] text-slate-500">
              Cada punto es una hora del top: posición horizontal = momento del año,
              vertical = margen (capacidad − demanda). Entre más abajo, más apretada la hora
              {hayNegativos && "; debajo de la línea roja la demanda superó la capacidad ofertada"}.
            </p>
          </div>
          <div className="h-[340px]">
            <ResponsiveContainer width="100%" height="100%">
              <ScatterChart margin={{ top: 10, right: 24, left: 0, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis
                  dataKey="t"
                  type="number"
                  domain={[ejeX.min, ejeX.max]}
                  ticks={ejeX.ticks}
                  tickFormatter={(ts) => MESES_G[new Date(Number(ts)).getMonth()] ?? ""}
                  tick={{ fontSize: 11, fill: "#475569" }}
                />
                <YAxis
                  dataKey="margen_mw"
                  type="number"
                  tick={{ fontSize: 11, fill: "#475569" }}
                  width={76}
                  tickFormatter={(v) => new Intl.NumberFormat("es-MX", { maximumFractionDigits: 0 }).format(Number(v))}
                  label={{
                    value: "Margen (MW)",
                    angle: -90,
                    position: "insideLeft",
                    style: { fontSize: 11, fill: "#64748b" },
                  }}
                />
                {hayNegativos && (
                  <ReferenceLine y={0} stroke="#dc2626" strokeDasharray="5 3" strokeWidth={1.5} />
                )}
                <Tooltip
                  cursor={{ strokeDasharray: "3 3" }}
                  content={({ active, payload }) => {
                    if (!active || !payload?.length) return null;
                    const p = payload[0].payload as MargenCapacidadFila & { pos: number };
                    return (
                      <div className="bg-white border rounded shadow px-3 py-2 text-xs">
                        <FichaHC f={p} titulo={`#${p.pos} · ${p.sistema}`} estado={estadoMro(p)} />
                        <div className="mt-1.5 pt-1 border-t text-slate-500">
                          Capacidad {num(p.capacidad_mw, 0)}
                          {p.intercambio_mw != null && ` · Interc. ${num(p.intercambio_mw, 0)}`}
                          {" · Demanda "}{num(p.demanda_mw, 0)} MW
                        </div>
                      </div>
                    );
                  }}
                />
                {[...puntosPorSistema.entries()].map(([s, pts]) => (
                  <Scatter
                    key={s}
                    name={s}
                    data={pts}
                    fill={COLOR_SISTEMA[s] ?? "#64748b"}
                    fillOpacity={0.75}
                  />
                ))}
              </ScatterChart>
            </ResponsiveContainer>
          </div>
          {puntosPorSistema.size > 1 && (
            <div className="flex gap-3 justify-center text-xs text-slate-600">
              {[...puntosPorSistema.keys()].map((s) => (
                <span key={s} className="inline-flex items-center gap-1">
                  <span
                    className="inline-block w-2.5 h-2.5 rounded-full"
                    style={{ backgroundColor: COLOR_SISTEMA[s] ?? "#64748b" }}
                  />
                  {s}
                </span>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
